import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Breadcrumb from '../components/Breadcrumb';
import { BookOpen, Plus, Edit, Trash2, Eye } from 'lucide-react';

const LibraryAdmin = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.email?.endsWith('@yomev.org')) {
      navigate('/library');
      return;
    }
    loadDocuments();
  }, [user, navigate]);

  const loadDocuments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/library/documents`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setDocuments(data);
      }
    } catch (error) {
      console.error('Dokümanlar yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'Taslak': 'bg-gray-100 text-gray-800',
      'Yayında': 'bg-green-100 text-green-800',
      'Arşiv': 'bg-red-100 text-red-800'
    };
    return badges[status] || badges['Taslak'];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <Breadcrumb 
        items={[
          { label: 'Doküman Kütüphanesi', path: '/library' },
          { label: 'Yönetim', path: null }
        ]} 
      />

      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">📚 Kütüphane Yönetimi</h1>
          <p className="text-gray-600">YÖMEV doküman yönetim paneli</p>
        </div>
        <button
          onClick={() => navigate('/admin/library/documents/new')}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-lg"
        >
          <Plus size={20} />
          Yeni Doküman Oluştur
        </button>
      </div>

      {documents.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <BookOpen className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Henüz doküman eklenmemiş</h3>
          <p className="text-gray-500 mb-6">Başlamak için "Yeni Doküman Oluştur" butonuna tıklayın</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {documents.map((doc) => (
            <div key={doc.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl">{doc.category === 'Plan Örneği' ? '📘' : '📄'}</span>
                    <h3 className="text-xl font-semibold text-gray-900">{doc.title}</h3>
                    <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusBadge(doc.status)}`}>
                      {doc.status}
                    </span>
                  </div>
                  
                  <div className="flex gap-4 text-sm text-gray-600 mb-3">
                    <span>Kategori: {doc.category}</span>
                    <span>Versiyon: v{doc.currentVersion}</span>
                    <span>Görüntüleme: {doc.viewCount}</span>
                    <span>İndirme: {doc.downloadCount}</span>
                  </div>
                  
                  <p className="text-gray-600 line-clamp-2">{doc.description}</p>
                </div>
                
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => navigate(`/library/${doc.id}`)}
                    className="p-2 text-gray-600 hover:bg-gray-50 rounded"
                    title="Görüntüle"
                  >
                    <Eye size={20} />
                  </button>
                  <button
                    onClick={() => navigate(`/admin/library/documents/${doc.id}/edit`)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                    title="Düzenle"
                  >
                    <Edit size={20} />
                  </button>
                  <button
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                    title="Sil"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LibraryAdmin;
